import { ComponentProps, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { Vertical } from 'atoms/layout/flex';
import { glossary } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

import {
  HighlightExtraElementWrapper,
  Props as HighlightExtraElementWrapperProps,
} from '../HighlightExtraElementWrapper';
import { OnboardingButton } from '../OnboardingButton';

const StyledDiv = styled(HighlightExtraElementWrapper)`
  align-items: flex-end;
`;

const BlurredMessage = styled.div`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-nd-100);
  backdrop-filter: blur(10px);
  text-align: center;
`;

const DesktopButton = styled(OnboardingButton)`
  display: none;
  @media ${tabletAndAbove} {
    display: block;
  }
`;

const PositionedButton = styled(HighlightExtraElementWrapper)`
  display: none;
  @media ${tabletAndAbove} {
    display: block;
  }
`;

type Props = {
  children: ReactNode;
  buttonProps?: {
    anchor?: 'top' | 'bottom';
    align?: 'start' | 'center' | 'end';
  } & Partial<ComponentProps<typeof OnboardingButton>>;
};

const MobileFooterButtons = styled.div`
  position: absolute;
  bottom: var(--double-unit);
  left: var(--double-unit);
  right: var(--double-unit);
  @media ${tabletAndAbove} {
    display: none;
  }
`;

export const ExtraMessageWithBlurredBg = ({
  children,
  className,
  buttonProps,
  ...rest
}: Props & HighlightExtraElementWrapperProps) => {
  const { ready = true } = rest?.positionProps || {};

  if (!ready) {
    return null;
  }

  const isPositionedButton = buttonProps?.anchor && buttonProps?.align;

  return (
    <>
      <StyledDiv {...rest} className={className}>
        <Vertical gap={1} center>
          <BlurredMessage>{children}</BlurredMessage>
          {!isPositionedButton && !!buttonProps && (
            <div>
              <DesktopButton size="medium" color="secondary" {...buttonProps}>
                {buttonProps?.children || (
                  <FormattedMessage {...glossary.continue} />
                )}
              </DesktopButton>
            </div>
          )}
        </Vertical>
      </StyledDiv>
      {!!buttonProps && (
        <>
          <MobileFooterButtons>
            <Button fullWidth size="large" color="secondary" {...buttonProps}>
              {buttonProps.children || (
                <FormattedMessage {...glossary.continue} />
              )}
            </Button>
          </MobileFooterButtons>
          {!!isPositionedButton && (
            <PositionedButton
              {...rest}
              align={buttonProps.align}
              anchor={buttonProps.anchor}
            >
              <DesktopButton size="large" color="secondary" {...buttonProps}>
                {buttonProps.children || (
                  <FormattedMessage {...glossary.continue} />
                )}
              </DesktopButton>
            </PositionedButton>
          )}
        </>
      )}
    </>
  );
};
